import { ACH_LADDERS } from "../model/types";
import type { AchCatalog, AchForm, AchLadder, AchLadderDef, AchievementsView } from "../model/types";

/**
 * Шкала «Тренировки» (ADDENDUM 30.09 §6–§7) — всё по плану тренировок: общий
 * уровень (workouts) — недели, выполненные по плану на 80 % и больше, и четыре
 * лестницы практик по плану — ходьба и бег (dyn), статика (stat), тепло (therm:
 * баня и горячая вода), пост (fast). Пороги, названия и картинки — в каталоге
 * (levels.ladders); здесь — как их читать.
 *
 * Картинка зависит от пола человека (f — женская; мужская и неизвестная — m) и у
 * статики и тепла — от упражнения, которое у него чаще (variant). Сервер
 * присылает id картинки (img): по нему и узнаём пол и упражнение — так картинка
 * всегда та, что выбрал сервер. Нет img (или каталог его не знает: «солнце» §6 из
 * каталога ушло) — форма вида и variant, а незнакомое упражнение — первое в
 * каталоге, как у сервера (ladder_img).
 */

export const isLadder = (key: string): key is AchLadder => (ACH_LADDERS as readonly string[]).includes(key);

export const ladderDef = (catalog: AchCatalog, key: string): AchLadderDef | undefined =>
    isLadder(key) ? catalog.levels.ladders?.[key] : undefined;

/** Упражнение картинки: знакомое лестнице — оно, иначе первое; у лестниц без упражнений — нет. */
export const ladderVariant = (def: AchLadderDef, x?: string): string | undefined => {
    const variants = def.variants || [];
    if (!variants.length) return undefined;
    return x && variants.includes(x) ? x : variants[0];
};

/** id картинки уровня n в форме и упражнении человека (как site_server achievements_catalog.ladder_img). */
export const ladderImg = (def: AchLadderDef, n: number, f?: AchForm | null, x?: string): string | undefined => {
    const lv = def.levels[n - 1];
    if (!lv) return undefined;
    const form = f === "f" ? "f" : "m";
    const variant = ladderVariant(def, x);
    return lv.img[variant ? `${variant}-${form}` : form];
};

/** Пол и упражнение картинки по её id из каталога; незнакомый id — null. */
export const lookOfImg = (def: AchLadderDef, img: string | undefined): { f: AchForm; x?: string } | null => {
    if (!img) return null;
    for (const lv of def.levels) {
        for (const [k, id] of Object.entries(lv.img)) {
            if (id !== img) continue;
            const parts = k.split("-");
            const f = parts[parts.length - 1];
            if (f !== "f" && f !== "m") return null;
            return parts.length > 1 ? { f, x: parts.slice(0, -1).join("-") } : { f };
        }
    }
    return null;
};

/**
 * Как рисовать лестницу этого человека: {f?, x?} для цели карточки (без пустых полей).
 * Упражнение (x) — только когда лестница начата (уровень ≥ 1): до первой практики
 * картинка — первая по умолчанию (как у сервера), и карточка не говорит «то, что у вас чаще».
 */
export const ladderLook = (
    view: AchievementsView,
    key: AchLadder,
    catalog: AchCatalog,
): { f?: AchForm; x?: string } => {
    const def = ladderDef(catalog, key);
    if (!def) return {};
    const state = view.ladders?.[key];
    const byImg = lookOfImg(def, state?.img);
    const f = byImg ? byImg.f : view.form || undefined;
    const raw = byImg ? byImg.x : state?.variant;
    const started = ladderLevel(view, key, def.levels.length) >= 1;
    const x = started && raw && ladderVariant(def, raw) === raw ? raw : undefined;
    return { ...(f ? { f } : {}), ...(x ? { x } : {}) };
};

/**
 * Взятый уровень лестницы 0…6: из ladders, у общего — ещё из levels.workouts
 * (его место в ряду уровней). Нет ничего — 0.
 */
export const ladderLevel = (view: AchievementsView, key: AchLadder, top: number): number => {
    const raw = view.ladders?.[key]?.level ?? (key === "workouts" ? view.levels?.workouts?.level : undefined);
    return typeof raw === "number" && Number.isFinite(raw) ? Math.max(0, Math.min(top, Math.floor(raw))) : 0;
};

/** Значение лестницы (практики или недели по плану) — только в своём виде. */
export const ladderValue = (view: AchievementsView, key: AchLadder): number | undefined => {
    const v = view.self ? view.ladders?.[key]?.value : undefined;
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
};

/** Остаток до порога — до десятых, как значения сервера. */
export const gapOf = (threshold: number, value: number): number => Math.round((threshold - value) * 10) / 10;
